import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, flow } from 'lodash';
import { getEmptyAnswer } from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';

import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import { SURVEY_TYPE_PEEQ } from 'containers/Survey/constants';

const conceptTestingGenerator = ({ template, variables, surveyType }) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const otherProductCategories = get(
    find(variables, ['name', 'other_product_category']),
    'value',
    [],
  );
  const yourProduct = get(
    find(variables, ['name', 'your_product']),
    'value',
    {},
  );
  const headline = get(find(variables, ['name', 'headline']), 'value', '');
  const yourProductInfo = get(
    find(variables, ['name', 'your_product_info']),
    'value',
    '',
  );
  const yourProductPrice = get(
    find(variables, ['name', 'your_product_price']),
    'value',
    [],
  );
  const features = get(find(variables, ['name', 'feature']), 'value', []);
  const statements = get(find(variables, ['name', 'statements']), 'value', []);
  const period = get(find(variables, ['name', 'period']), 'value', '');
  const includeFast = get(
    find(variables, ['name', 'include_fast']),
    'value',
    false,
  );

  // replace general texts
  const replacePeriod = (value) =>
    value.replace(/\{\{period\}\}/gi, escapeHTML(period));
  const replaceProductCategory = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );
  const replaceHeadline = (value) =>
    value.replace(/\{\{headline\}\}/gi, escapeHTML(headline));
  const replaceProductInfo = (value) =>
    value.replace(/\{\{your_product_info\}\}/gi, escapeHTML(yourProductInfo));
  const replaceProductPrice = (value) =>
    value.replace(
      /\{\{your_product_price\}\}/gi,
      escapeHTML(yourProductPrice[0]?.text),
    );
  const replaceYourProduct = (value) =>
    value.replace(/\{\{your_product\}\}/gi, escapeHTML(yourProduct.text));

  const replaceAllTexts = flow([
    replaceProductCategory,
    replacePeriod,
    replaceHeadline,
    replaceProductInfo,
    replaceProductPrice,
    replaceYourProduct,
  ]);

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceAllTexts,
      htmlPrompt: replaceAllTexts,
      body: replaceAllTexts,
    },
  );

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: questionnaireTemplate,
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });
  // GET RELATED QUESTIONS GROUPS
  const { nodes: conceptTestingTemplateQuestions } =
    mapQuestionsAndGroupsAsNodes(questionnaireTemplate);

  const questionWithCategories = conceptTestingTemplateQuestions[0];
  const questionFast = conceptTestingTemplateQuestions[1];
  const questionSlow = conceptTestingTemplateQuestions[2];
  const questionDescription = conceptTestingTemplateQuestions[3];
  const questionWithImage = conceptTestingTemplateQuestions[4];
  const questionWithStatements = conceptTestingTemplateQuestions[16];
  const questionWithFeatures = conceptTestingTemplateQuestions[17];
  const questionGoingToPrice = conceptTestingTemplateQuestions[7];
  const questionSinglePrice = conceptTestingTemplateQuestions[8];
  const questionMultiPrice = conceptTestingTemplateQuestions[9];
  const questionAfterPrice = conceptTestingTemplateQuestions[10];
  // SET UP
  // if includeFast is true, remove q3 and make q1 point to q2
  // if includeFast is false, remove q2 and make q1 point to q3
  // if yourProductPrice.length === 0, remove questionSinglePrice and questionMultiPrice (make q7 point to q10)
  // if yourProductPrice.length === 1, remove questionMultiPrice (make q8 point to q10)

  // SET UP QUESTION 1
  const q1Answers = [
    {
      ...questionWithCategories.answers[0],
      body: productCategory.text,
      media: productCategory.media,
    },
    ...otherProductCategories.map((otherCategory) => ({
      ...getEmptyAnswer(true),
      body: otherCategory.text,
      media: otherCategory.media,
    })),
  ];
  Survey.questionnaire.updateQuestion({
    ...questionWithCategories,
    answers: q1Answers,
  });

  if (includeFast) {
    Survey.questionnaire.removeQuestion({
      refId: questionSlow.refId,
    });
    Survey.questionnaire.setQuestionDefaultOutcome(questionWithCategories, {
      refId: questionFast.refId,
    });
  } else {
    Survey.questionnaire.removeQuestion({
      refId: questionFast.refId,
    });
    Survey.questionnaire.setQuestionDefaultOutcome(questionWithCategories, {
      refId: questionSlow.refId,
    });
  }

  // add the question images
  Survey.questionnaire.updateQuestion({
    ...questionDescription,
    media: yourProduct.media,
  });
  Survey.questionnaire.updateQuestion({
    ...questionWithImage,
    media: yourProduct.media,
  });

  // fill the matrix questions statements
  Survey.questionnaire.updateQuestion({
    ...questionWithStatements,
    answers: statements.map((statement) => ({
      ...getEmptyAnswer(true),
      body: statement.text,
    })),
  });

  Survey.questionnaire.updateQuestion({
    ...questionWithFeatures,
    answers: features.map((feature) => ({
      ...getEmptyAnswer(true),
      body: `<div><strong>${feature}</strong></div>`,
    })),
  });

  // decide which question to show
  // depending on the number of prices
  if (yourProductPrice.length === 0) {
    Survey.questionnaire.setQuestionDefaultOutcome(questionGoingToPrice, {
      refId: questionAfterPrice.refId,
    });
    Survey.questionnaire.removeQuestion({ refId: questionSinglePrice.refId });
    Survey.questionnaire.removeQuestion({ refId: questionMultiPrice.refId });
  } else if (yourProductPrice.length === 1) {
    Survey.questionnaire.setQuestionDefaultOutcome(questionSinglePrice, {
      refId: questionAfterPrice.refId,
    });
    Survey.questionnaire.removeQuestion({ refId: questionMultiPrice.refId });
  } else if (yourProductPrice.length > 1) {
    Survey.questionnaire.updateQuestion({
      ...questionMultiPrice,
      answers: yourProductPrice.map((price) => ({
        ...getEmptyAnswer(true),
        body: `<div>If this product was priced at <strong>${
          price.text
        }</strong></div>`,
      })),
    });
    Survey.questionnaire.setQuestionDefaultOutcome(questionGoingToPrice, {
      refId: questionMultiPrice.refId,
    });
    Survey.questionnaire.removeQuestion({
      refId: questionSinglePrice.refId,
    });
  }

  return { state: Survey.state };
};

export default conceptTestingGenerator;
